/**
 * FS-678 — Per-subnetwork favicon upload + URL picker.
 *
 * Composition:
 *   - Subnetwork id selector (free-text entry; deep-linkable via ?id=)
 *   - Favicon preview thumbnail (current state)
 *   - Upload (file input) — turns PNG/ICO/SVG into base64 and PUTs it
 *   - URL input alternative — PUTs an https cdn url
 *   - Delete button — clears the favicon for the current subnetwork
 *
 * Data layer: TanStack Query + Sonner for feedback. No modal libraries; the
 * delete confirm is a `window.confirm` to keep the feature inside its lane.
 *
 * Brand: emerald→sky headline gradient, zinc borders, 6–8px rounded, light
 * theme only, no emojis, no competitor names.
 */
import { useCallback, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	FAVICON_MIMES,
	MAX_BASE64_CHARS,
	MAX_RAW_BYTES,
	deleteSubnetworkFavicon,
	fetchSubnetworkFavicon,
	fileToBase64,
	formatFaviconSize,
	inferMimeFromFilename,
	isFaviconMime,
	isHttpsUrlForFavicon,
	putSubnetworkFavicon,
	subnetworkFaviconQueryKeys,
	type SubnetworkFavicon,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface SubnetworkFaviconPageProps {
	/** Optional initial subnetwork id (route passes ?id=). */
	initialSubnetworkId?: string;
}

export function SubnetworkFaviconPage(props: SubnetworkFaviconPageProps = {}) {
	const [input, setInput] = useState(props.initialSubnetworkId ?? "");
	const [selected, setSelected] = useState<string>(
		props.initialSubnetworkId ?? "",
	);

	const handleSelect = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			const trimmed = input.trim();
			setSelected(trimmed);
		},
		[input],
	);

	return (
		<section className="flex flex-col gap-5">
			<header className="flex flex-col gap-1">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-2xl font-semibold text-transparent">
					Subnetwork favicon
				</h1>
				<p className="text-sm text-zinc-500">
					Upload a .png, .ico, or .svg favicon per managed sub-network. Max{" "}
					{formatFaviconSize(MAX_RAW_BYTES)} decoded.
				</p>
			</header>

			<form
				onSubmit={handleSelect}
				className={`${CARD} flex flex-col gap-3 p-4`}
			>
				<label className={LABEL} htmlFor="subnetwork-id">
					Sub-network id
				</label>
				<div className="flex flex-wrap items-center gap-2">
					<input
						id="subnetwork-id"
						className={`${INPUT} max-w-md`}
						value={input}
						onChange={(e) => setInput(e.target.value)}
						placeholder="sn_abc123"
						autoComplete="off"
						spellCheck={false}
					/>
					<button type="submit" className={BTN_PRIMARY}>
						Load
					</button>
					{selected ? (
						<span className="text-xs text-zinc-500">
							Viewing{" "}
							<code className="rounded-[4px] bg-zinc-100 px-1 py-0.5">
								{selected}
							</code>
						</span>
					) : null}
				</div>
			</form>

			{selected ? (
				<FaviconEditor subnetworkId={selected} />
			) : (
				<div className={`${CARD} p-6 text-center text-sm text-zinc-500`}>
					Enter a sub-network id above to manage its favicon.
				</div>
			)}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Editor — live query against the api + write-side mutations.
// ---------------------------------------------------------------------------

interface FaviconEditorProps {
	subnetworkId: string;
}

function FaviconEditor({ subnetworkId }: FaviconEditorProps) {
	const queryClient = useQueryClient();

	const { data, isLoading, isError } = useQuery({
		queryKey: subnetworkFaviconQueryKeys.one(subnetworkId),
		queryFn: () => fetchSubnetworkFavicon(subnetworkId),
	});

	const current: SubnetworkFavicon | null = useMemo(
		() => data?.favicon ?? null,
		[data],
	);

	const invalidate = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: subnetworkFaviconQueryKeys.one(subnetworkId),
		});
	}, [queryClient, subnetworkId]);

	const putMut = useMutation({
		mutationFn: putSubnetworkFavicon,
		onSuccess: () => {
			toast.success("Favicon saved");
			invalidate();
		},
		onError: (err: unknown) => {
			const msg =
				err instanceof Error ? err.message : "Failed to save favicon";
			toast.error(msg);
		},
	});

	const delMut = useMutation({
		mutationFn: () => deleteSubnetworkFavicon(subnetworkId),
		onSuccess: () => {
			toast.success("Favicon cleared");
			invalidate();
		},
		onError: (err: unknown) => {
			const msg =
				err instanceof Error ? err.message : "Failed to delete favicon";
			toast.error(msg);
		},
	});

	const [urlInput, setUrlInput] = useState("");
	const [urlBusy, setUrlBusy] = useState(false);
	const [fileBusy, setFileBusy] = useState(false);

	const submitUrl = useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			const url = urlInput.trim();
			if (!isHttpsUrlForFavicon(url)) {
				toast.error("Favicon URL must start with https://");
				return;
			}
			const inferred = inferMimeFromFilename(url);
			setUrlBusy(true);
			try {
				await putMut.mutateAsync(
					inferred
						? { subnetwork_id: subnetworkId, url, mime: inferred }
						: { subnetwork_id: subnetworkId, url },
				);
				setUrlInput("");
			} finally {
				setUrlBusy(false);
			}
		},
		[putMut, subnetworkId, urlInput],
	);

	const submitFile = useCallback(
		async (file: File) => {
			const inferred = inferMimeFromFilename(file.name);
			const mime = isFaviconMime(file.type)
				? file.type
				: inferred ?? null;
			if (!mime) {
				toast.error(
					`Unsupported file type. Use ${FAVICON_MIMES.join(", ")}.`,
				);
				return;
			}
			if (file.size > MAX_RAW_BYTES) {
				toast.error(
					`File too large (max ${formatFaviconSize(MAX_RAW_BYTES)}).`,
				);
				return;
			}

			setFileBusy(true);
			try {
				const base64 = await fileToBase64(file);
				if (!base64) {
					toast.error("Could not read file");
					return;
				}
				if (base64.length > MAX_BASE64_CHARS) {
					toast.error(
						`File too large (max ${formatFaviconSize(MAX_RAW_BYTES)}).`,
					);
					return;
				}
				await putMut.mutateAsync({
					subnetwork_id: subnetworkId,
					base64,
					mime,
				});
			} finally {
				setFileBusy(false);
			}
		},
		[putMut, subnetworkId],
	);

	const onDelete = useCallback(() => {
		if (!current) return;
		if (
			!window.confirm(
				`Clear favicon for ${subnetworkId}? The managed agency portal will fall back to the parent brand.`,
			)
		) {
			return;
		}
		delMut.mutate();
	}, [current, delMut, subnetworkId]);

	if (isLoading) {
		return (
			<div className={`${CARD} p-6 text-sm text-zinc-500`}>Loading…</div>
		);
	}
	if (isError) {
		return (
			<div className={`${CARD} p-6 text-sm text-red-600`}>
				Failed to load favicon. Try again in a moment.
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			{/* Preview card */}
			<div className={`${CARD} flex items-center gap-4 p-4`}>
				<FaviconThumb fav={current} />
				<div className="flex min-w-0 flex-1 flex-col">
					<span className={LABEL}>Current favicon</span>
					{current ? (
						<>
							<span className="mt-1 truncate text-xs text-zinc-600">
								<code>{truncateUrl(current.url)}</code>
							</span>
							<span className="text-xs text-zinc-500">
								{current.mime} · {formatFaviconSize(current.size_bytes)}
							</span>
						</>
					) : (
						<span className="mt-1 text-xs text-zinc-500">
							No favicon set — the parent brand favicon will be served.
						</span>
					)}
				</div>
				<button
					type="button"
					className={BTN_DANGER}
					onClick={onDelete}
					disabled={!current || delMut.isPending}
				>
					{delMut.isPending ? "Clearing…" : "Clear"}
				</button>
			</div>

			{/* Upload via file input */}
			<div className={`${CARD} flex flex-col gap-3 p-4`}>
				<span className={LABEL}>Upload a favicon file</span>
				<input
					type="file"
					accept={FAVICON_MIMES.join(",")}
					className="block w-full text-sm text-zinc-700 file:mr-3 file:rounded-[6px] file:border file:border-zinc-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-zinc-700 hover:file:bg-zinc-50"
					onChange={(e) => {
						const file = e.target.files?.[0] ?? null;
						if (file) {
							submitFile(file).finally(() => {
								e.target.value = "";
							});
						}
					}}
					disabled={fileBusy || putMut.isPending}
				/>
				<p className="text-xs text-zinc-500">
					PNG / ICO / SVG only. Max {formatFaviconSize(MAX_RAW_BYTES)}{" "}
					decoded.
				</p>
			</div>

			{/* URL alternative */}
			<form
				onSubmit={submitUrl}
				className={`${CARD} flex flex-col gap-3 p-4`}
			>
				<span className={LABEL}>Or paste an https CDN URL</span>
				<div className="flex flex-wrap items-center gap-2">
					<input
						type="url"
						className={`${INPUT} max-w-xl`}
						value={urlInput}
						onChange={(e) => setUrlInput(e.target.value)}
						placeholder="https://cdn.example.com/favicon.ico"
						autoComplete="off"
						spellCheck={false}
					/>
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={urlBusy || putMut.isPending}
					>
						{urlBusy ? "Saving…" : "Save URL"}
					</button>
				</div>
			</form>

			{/* Footer help */}
			<div className="flex items-center justify-end gap-2">
				<button
					type="button"
					className={BTN_NEUTRAL}
					onClick={invalidate}
					disabled={putMut.isPending || delMut.isPending}
				>
					Refresh
				</button>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Preview thumb — renders the favicon if url is renderable, else a placeholder.
// ---------------------------------------------------------------------------

function FaviconThumb({ fav }: { fav: SubnetworkFavicon | null }) {
	const box =
		"flex h-14 w-14 shrink-0 items-center justify-center rounded-[6px] border border-zinc-200 bg-zinc-50";
	if (!fav) {
		return <div className={box}>{/* placeholder: no icon */}</div>;
	}
	return (
		<div className={box}>
			<img
				src={fav.url}
				alt="favicon preview"
				className="h-10 w-10 object-contain"
				onError={(e) => {
					(e.currentTarget as HTMLImageElement).style.display = "none";
				}}
			/>
		</div>
	);
}

function truncateUrl(url: string): string {
	if (url.length <= 80) return url;
	return `${url.slice(0, 40)}…${url.slice(-30)}`;
}
